import { withTranslation } from 'react-i18next';
import { Container } from '@/ui';
import { ProfileViewerSkeleton } from '@/components/common/molecules/ProfileViewer';
import { Flex, message, Popconfirm, Tree, Typography } from 'antd';
import { useMemo } from 'react';
import { CloseCircleOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { COLORS } from '@/utils/static';
import { getErrorMessage } from '@/utils/helpers';

const roleLabels = {
  AGENT: 'Agent',
  RECOMMENDER: 'Assistant',
  PERSONAL: 'Investor'
};

export const AccountsTree = withTranslation()(({
  t,
  accounts,
  emptyTitle = t('Nothing to display'),
  adminView,
  onDeleteRecommender,
  onDeleteAgent
}) => {
  const { id } = useParams();
  const { investor } = useSelector((state) => state);
  const [messageApi, messageContext] = message.useMessage();
  const navigate = useNavigate();
  const { error, list, isLoading } = accounts;

  function getTree(list, modifiedList, level, currentIndex) {
    if (!list) return;

    list.forEach((item, index) => {
      if (item)
        modifiedList.push({
          title: `${item.firstName} ${item.lastName} (${t(roleLabels[item.kycRole])})`,
          key: level === 0 ? `0-${index}` : `${currentIndex}-${index}`,
          icon: <UserOutlined />,
          role: item.kycRole,
          id: item.id
        });
      if (item.subAgents) {
        modifiedList[index] = { ...modifiedList[index], children: [] };
        getTree(item.subAgents, modifiedList[index].children, level + 1, modifiedList[index].key);
      }
      if (item.subRecommenders) {
        modifiedList[index] = { ...modifiedList[index], children: [] };
        getTree(
          item.subRecommenders,
          modifiedList[index].children,
          level + 1,
          modifiedList[index].key
        );
      }
    });

    return modifiedList;
  }

  const treeList = useMemo(() => {
    if (list) return getTree(list, [], 0, 0);
  }, [list, t]);

  const onSelect = async (investorId) => {
    if (adminView) {
      navigate(`/investor/${investorId}`);
    } else {
      navigate(`/referral/${investorId}/questionnaires`);
    }
  };

  const handleDelete = async (investorId, deleteId) => {
    try {
      if (investor.accountRole === 'RECOMMENDER') {
        const res = await onDeleteRecommender({
          investorId: investorId,
          ...(onDeleteRecommender && { recommenderId: deleteId })
        });

        if (res) messageApi.success(t('Referral connection is deleted'));
      }

      if (investor.accountRole === 'AGENT') {
        const res = await onDeleteAgent({
          investorId: investorId,
          ...(onDeleteAgent && { agentId: deleteId })
        });

        if (res) messageApi.success(t('Referral connection is deleted'));
      }
    } catch (e) {
      console.error(e);
      messageApi.error(t(getErrorMessage(e)));
    }
  };

  if (isLoading)
    return (
      <Container $marginTop={16}>
        {[1, 2, 3].map((item) => (
          <Container $marginBottom={12} key={item}>
            <ProfileViewerSkeleton />
          </Container>
        ))}
      </Container>
    );

  if (error) return <Typography.Text type={'danger'}>{t('An error occurred')}</Typography.Text>;
  if (!list || list.length < 1) {
    return (
      <Container $marginTop={16}>
        <Typography.Text>{emptyTitle}</Typography.Text>
      </Container>
    );
  }

  return (
    <Container $marginTop={16}>
      {messageContext}
      <Tree
        showLine
        treeData={treeList}
        titleRender={(item) => {
          return (
            <Flex gap={5}>
              <UserOutlined style={{ marginBottom: '4px' }} />
              <span onClick={() => onSelect(item.id)}>{item.title ?? ''}</span>
              {(onDeleteRecommender || onDeleteAgent) &&
                adminView &&
                item.key.match('^0-[0-9]{1}$') && (
                  <Popconfirm
                    placement='topRight'
                    title={t('Are you sure you want to delete this referral connection?')}
                    onConfirm={() => handleDelete(item.id, id)}
                    okText={t('Yes')}
                    cancelText={t('No')}
                  >
                    <DeleteIcon>
                      <CloseCircleOutlined />
                    </DeleteIcon>
                  </Popconfirm>
                )}
            </Flex>
          );
        }}
        style={{
          background: 'transparent'
        }}
      />
    </Container>
  );
});

const DeleteIcon = styled.span`
  width: 18px;
  height: 18px;
  font-size: 18px;
  color: ${COLORS.iconColor};
  margin-left: 5px;
  margin-bottom: 3px;
  cursor: pointer;

  &:hover {
    color: ${COLORS.greyLight};
  }
`;
